import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Icon from '../../components/AppIcon';
// Removed image/banner and footer buttons for a cleaner UI

const AIUniversityDetailsModal = ({ university, onClose, userProfile = {}, preferences = {} }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!university?.name) return;
      setLoading(true);
      setError(null);

      // Prefer data extracted from the main AI recommendations if present
      const hasDirectData = university?.details || university?.program_meant_for_you || university?.what_you_will_learn || university?.why_for_you;
      if (hasDirectData) {
        setData({
          details: university.details || {},
          why_for_you: university.why_for_you || [],
          program_meant_for_you: university.program_meant_for_you || {},
          what_you_will_learn: university.what_you_will_learn || [],
        });
        setLoading(false);
        return;
      }

      // Fallback: call backend endpoint to generate details
      try {
        const res = await axios.post('/api/university-details', {
          universityName: university.name,
          userProfile,
          preferences,
        });
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          throw new Error(res.data?.error || 'Failed to fetch details');
        }
      } catch (err) {
        console.error('AI details error:', err);
        setError(err.message || 'Failed to load details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [university, userProfile, preferences]);

  if (!university) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header (simplified) */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-card flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-foreground">{university.name}</h2>
            <p className="text-muted-foreground text-sm">{university.country || university.location}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 bg-muted rounded-full flex items-center justify-center hover:bg-muted/80 transition-smooth" aria-label="Close modal">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border overflow-x-auto flex-shrink-0">
          <div className="flex">
            {[{id:'details',label:'Details & Why',icon:'Info'},{id:'program',label:"Program & What You'll Learn",icon:'BookOpen'}].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-6 py-3 font-medium transition-smooth whitespace-nowrap text-sm ${activeTab===tab.id?'text-primary border-b-2 border-primary':'text-muted-foreground hover:text-foreground'}`}>
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                <p className="text-muted-foreground">Fetching personalized details...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 bg-error/10 border border-error/20 rounded-lg">
              <p className="text-sm text-error">{error}</p>
            </div>
          ) : data ? (
            <>
              {activeTab === 'details' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Overview</h3>
                    <p className="text-muted-foreground leading-relaxed">{data.details?.overview}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="text-sm text-muted-foreground mb-1">Location</div>
                      <div className="text-foreground font-medium">{data.details?.location}</div>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="text-sm text-muted-foreground mb-1">Ranking</div>
                      <div className="text-primary font-semibold">{data.details?.ranking}</div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Notable Strengths</h4>
                    <div className="flex flex-wrap gap-2">
                      {(data.details?.notable_strengths||[]).map((s, idx) => (
                        <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Why This Fits You</h3>
                    <ul className="space-y-2">
                      {(data.why_for_you||[]).map((pt, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{pt}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'program' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">Program Meant For You</h3>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">{data.program_meant_for_you?.name}</span>
                      <span className="text-sm text-primary">{data.program_meant_for_you?.degree}</span>
                    </div>
                    <p className="text-muted-foreground">{data.program_meant_for_you?.description}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Specializations</h4>
                    <div className="flex flex-wrap gap-2">
                      {(data.program_meant_for_you?.specializations||[]).map((spec, idx) => (
                        <span key={idx} className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">{spec}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">What You'll Learn</h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {(data.what_you_will_learn||[]).map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Icon name="Check" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Footer removed for a cleaner, focused modal */}
      </div>
    </div>
  );
};

export default AIUniversityDetailsModal;
